import { useEffect } from 'react';
import { USER_ROLES } from 'src/common/constants';
import {
  useMessagesAPI,
  UseMessagesAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useMessagesAPI';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import {
  useNotesAPI,
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import {
  buildAndPostMessage,
  ChatInput,
  MessageRecipient,
} from 'src/utils/MessagingUtils/postMessageHelper';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';

export interface UseServiceRequestTabsOrchestratorArgs {
  serviceRequestId: string;
  user: any;
  serviceRequest: any;
}

export interface UseServiceRequestTabsOrchestratorReturn {
  messages: any[];
  isSending: boolean;
  documents: any[];
  loadingDocs: boolean;
  notesAPI: UseNotesAPIReturn;
  canEditAsset: boolean;
  canAddNote: boolean;
  canDelete: boolean;
  isHomeOwnerRealtorOccupant: boolean;
  isPropertyManagerRole: boolean;
  handlePostMessage: (chat: ChatInput) => Promise<void>;
  handleSaveDocument: (document: DocumentFormData) => Promise<void>;
  handleDeleteDocument: (document: any) => Promise<void>;
}

export const useServiceRequestTabsOrchestrator = ({
  serviceRequestId,
  user,
  serviceRequest,
}: UseServiceRequestTabsOrchestratorArgs): UseServiceRequestTabsOrchestratorReturn => {
  const messagesAPI: UseMessagesAPIReturn = useMessagesAPI();

  const documentsAPI: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({
    itemData: 'Ticket',
    user,
    customUploadErrorMessage:
      'Failed to upload this document. Please try again.',
  });

  const notesAPI: UseNotesAPIReturn = useNotesAPI(user);

  const userRole: string = user?.roles?.[0]?.role?.name || '';

  const isHomeOwnerRealtorOccupant: boolean =
    userRole === USER_ROLES.HOME_OWNER ||
    userRole === USER_ROLES.REALTOR ||
    userRole === USER_ROLES.OCCUPANT;

  const isPropertyManagerRole: boolean =
    userRole === USER_ROLES.PROPERTY_DEVELOPER ||
    userRole === USER_ROLES.STRATA_MANAGER ||
    userRole === USER_ROLES.SERVICE_MANAGER ||
    userRole === USER_ROLES.SERVICE_PROVIDER;

  const canEditAsset: boolean = [
    USER_ROLES.ADMIN,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.PROPERTY_DEVELOPER,
  ].includes(userRole);

  const canAddNote: boolean = !(
    userRole === USER_ROLES.STRATA_MANAGER ||
    userRole === USER_ROLES.VENDOR ||
    userRole === USER_ROLES.HOME_OWNER ||
    userRole === USER_ROLES.REALTOR ||
    userRole === USER_ROLES.OCCUPANT
  );

  const canDelete: boolean = !(
    userRole === USER_ROLES.HOME_OWNER ||
    userRole === USER_ROLES.REALTOR ||
    userRole === USER_ROLES.OCCUPANT
  );

  const handleSaveDocument = async (
    document: DocumentFormData
  ): Promise<void> => {
    await documentsAPI.saveDocument(document, serviceRequestId, user.id);
  };

  const handleDeleteDocument = async (document: any): Promise<void> => {
    await documentsAPI.deleteDocument(document.id);
  };

  const getMessageRecipient = (): MessageRecipient => {
    if (!serviceRequest) {
      return { id: null, error: 'Service request data not available.' };
    }

    if (userRole === USER_ROLES.PROPERTY_DEVELOPER) {
      const reporterId = serviceRequest.reporter?.id;
      const homeOwnerId = serviceRequest.suite?.homeOwner?.id;

      if (!reporterId) {
        return { id: null, error: 'No reporter found.' };
      }

      if (reporterId === user.id) {
        if (!homeOwnerId) {
          return { id: null, error: 'No homeowner found.' };
        }

        return { id: homeOwnerId };
      }

      return { id: reporterId };
    }

    const propertyManagers =
      serviceRequest.suite?.property?.propertyManagers || [];

    if (propertyManagers.length === 0) {
      return { id: null, error: 'No property manager assigned.' };
    }

    return { id: propertyManagers[0]?.id };
  };

  const handlePostMessage = async (chat: ChatInput): Promise<void> => {
    const recipient: MessageRecipient = getMessageRecipient();
    const actionUrl: string = `app/service-requests/${serviceRequestId}/activeTab=1`;
    const ticketNumber: string = serviceRequest?.ticketId?.toString() || '';

    await buildAndPostMessage({
      chat,
      user,
      recipient,
      referenceId: serviceRequestId,
      referenceType: 'Ticket',
      actionUrl,
      extraFields: { TicketNumber: ticketNumber },
      postMessageToAPI: messagesAPI.postMessageToAPI,
    });
  };

  useEffect(() => {
    if (!user || !serviceRequestId) {
      return;
    }

    messagesAPI.clearMessages();
    void messagesAPI.fetchMessagesFromAPI(serviceRequestId);
    void documentsAPI.fetchServiceRequestDocuments(serviceRequestId);
    void notesAPI.fetchNotes(serviceRequestId);
  }, [serviceRequestId, user]);

  return {
    messages: messagesAPI.messages,
    isSending: messagesAPI.isSending,
    documents: documentsAPI.documents,
    loadingDocs: documentsAPI.loadingDocs,
    notesAPI,
    canEditAsset,
    canAddNote,
    canDelete,
    isHomeOwnerRealtorOccupant,
    isPropertyManagerRole,
    handlePostMessage,
    handleSaveDocument,
    handleDeleteDocument,
  };
};
